import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;
const WIDGET_ENABLED = false; // set to true to re-activate the Advisor On Duty popup
const AUTO_OPEN_DELAY_MS = 4000;
const SESSION_KEY = "ridgemont_advisor_widget_shown";

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [form, setForm] = useState({
    name: "",
    age: "",
    amount: "",
    timing: "immediate", // "immediate" | "defer"
    defer_years: "",
    email: "",
  });
  const [errorMsg, setErrorMsg] = useState("");

  // Auto-pop-up once per browser session (dismissible)
  useEffect(() => {
    if (!WIDGET_ENABLED) return;
    if (typeof window === "undefined") return;
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch (e) {
      // sessionStorage may be blocked; still allow the pop-up
    }
    const t = setTimeout(() => {
      setOpen(true);
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (e) {}
    }, AUTO_OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const SMS_DESTINATION = "3465905590";

  const buildSmsBody = () => {
    const incomeLine =
      form.timing === "immediate"
        ? "I need income immediately."
        : `I can defer income for ${form.defer_years.trim()} years.`;
    return (
      `My name is ${form.name.trim()}.\n` +
      `I am ${form.age.trim()}.\n` +
      `I would like an annuity for ${form.amount.trim()}.\n` +
      `${incomeLine}\n` +
      `Please send my proposal to ${form.email.trim()}.`
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!form.name.trim() || !form.age.trim() || !form.amount.trim() || !form.email.trim()) {
      setErrorMsg("Please fill in every field so we can prepare your proposal.");
      return;
    }
    if (form.timing === "defer" && !form.defer_years.trim()) {
      setErrorMsg("Please tell us how many years you can defer.");
      return;
    }

    setStatus("submitting");

    // Log the lead to backend (fire-and-forget for CRM tracking).
    try {
      fetch(`${API}/api/leads/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          age: form.age.trim(),
          amount: form.amount.trim(),
          timing: form.timing,
          defer_years: form.timing === "defer" ? form.defer_years.trim() : null,
          email: form.email.trim(),
          page: typeof window !== "undefined" ? window.location.pathname : "",
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (err) {
      // Ignore — the SMS deep-link is the primary action.
    }

    // Open the visitor's native Messages app pre-filled with the proposal text.
    const body = encodeURIComponent(buildSmsBody());
    // Cross-platform sms: URI (works on iOS and Android)
    const smsHref = `sms:${SMS_DESTINATION}?&body=${body}`;
    if (typeof window !== "undefined") {
      window.location.href = smsHref;
    }

    setStatus("success");
  };

  const reset = () => {
    setForm({ name: "", age: "", amount: "", timing: "immediate", defer_years: "", email: "" });
    setStatus("idle");
    setErrorMsg("");
  };

  return (
    <>
      {!WIDGET_ENABLED ? null : (
      <>
      {/* Launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="launcher"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={() => setOpen(true)}
            data-testid="chat-widget-launcher"
            className="fixed bottom-6 right-6 z-[9998] flex items-center gap-3 bg-[#0A192F] text-white pl-5 pr-6 py-4 shadow-2xl border-2 border-[#C5A059]/60 hover:border-[#C5A059] hover:scale-[1.03] transition-all rounded-full"
            aria-label="Talk to the annuity advisor on duty"
          >
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70 bg-[#C5A059]"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#C5A059]"></span>
            </span>
            <span className="text-base md:text-lg font-semibold tracking-wide">
              Advisor On Duty
            </span>
            <MessageCircle className="w-5 h-5 text-[#C5A059]" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Slide-in Card */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="card"
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            data-testid="chat-widget-card"
            className="fixed bottom-5 right-5 z-[9999] w-[92vw] max-w-[400px] bg-white shadow-2xl border border-[#E7E5E4] overflow-hidden"
            style={{ borderRadius: "16px" }}
          >
            {/* Header */}
            <div className="bg-[#0A192F] px-5 py-4 flex items-start justify-between relative overflow-hidden">
              <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-[#C5A059] to-[#E6C88B]" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70 bg-[#C5A059]"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#C5A059]"></span>
                  </span>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-semibold">
                    Annuity Advisor on Duty
                  </p>
                </div>
                <h3
                  className="text-white text-xl mt-1"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Request your proposal.
                </h3>
                <p className="text-white/60 text-xs mt-1">
                  Tell us the basics. We'll build your proposal and get it to you.
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-white/60 hover:text-white transition-colors -mt-1"
                aria-label="Close"
                data-testid="chat-widget-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 bg-[#FAFAF9]">
              {status === "success" ? (
                <div className="text-center py-6" data-testid="chat-widget-success">
                  <div className="w-12 h-12 mx-auto mb-4 bg-[#1B4D3E] rounded-full flex items-center justify-center">
                    <Check className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <h4 className="text-[#0A192F] text-lg font-medium mb-2">
                    Got it, {form.name.split(" ")[0]}.
                  </h4>
                  <p className="text-[#57534E] text-sm leading-relaxed">
                    An advisor is preparing your proposal. It will be sent to{" "}
                    <span className="text-[#0A192F] font-medium">{form.email}</span>.
                  </p>
                  <button
                    onClick={reset}
                    className="mt-6 text-xs text-[#0A192F] underline hover:text-[#C5A059] transition-colors"
                    data-testid="chat-widget-send-another"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3" data-testid="chat-widget-form">
                  <div>
                    <label className="text-xs uppercase tracking-wide text-[#57534E] font-semibold mb-1 block">
                      Full name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Jane Doe"
                      className="bg-white border-[#E7E5E4]"
                      data-testid="chat-input-name"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-wide text-[#57534E] font-semibold mb-1 block">
                        Age <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="number"
                        min="18"
                        max="100"
                        required
                        value={form.age}
                        onChange={(e) => setForm({ ...form, age: e.target.value })}
                        placeholder="65"
                        className="bg-white border-[#E7E5E4]"
                        data-testid="chat-input-age"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wide text-[#57534E] font-semibold mb-1 block">
                        Amount <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        required
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        placeholder="$250,000"
                        className="bg-white border-[#E7E5E4]"
                        data-testid="chat-input-amount"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wide text-[#57534E] font-semibold mb-1 block">
                      Income timing <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, timing: "immediate", defer_years: "" })}
                        className={`px-3 py-2 text-sm border transition-all ${
                          form.timing === "immediate"
                            ? "bg-[#0A192F] text-white border-[#0A192F]"
                            : "bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#0A192F]/40"
                        }`}
                        data-testid="chat-timing-immediate"
                      >
                        Immediately
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, timing: "defer" })}
                        className={`px-3 py-2 text-sm border transition-all ${
                          form.timing === "defer"
                            ? "bg-[#0A192F] text-white border-[#0A192F]"
                            : "bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#0A192F]/40"
                        }`}
                        data-testid="chat-timing-defer"
                      >
                        I can defer
                      </button>
                    </div>
                    {form.timing === "defer" && (
                      <div className="mt-2">
                        <Input
                          type="number"
                          min="1"
                          max="40"
                          required
                          value={form.defer_years}
                          onChange={(e) => setForm({ ...form, defer_years: e.target.value })}
                          placeholder="Defer for how many years? (e.g., 5)"
                          className="bg-white border-[#E7E5E4]"
                          data-testid="chat-input-defer-years"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wide text-[#57534E] font-semibold mb-1 block">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="jane@example.com"
                      className="bg-white border-[#E7E5E4]"
                      data-testid="chat-input-email"
                    />
                    <p className="text-[10px] text-[#57534E]/60 mt-1">
                      Your proposal will be sent here.
                    </p>
                  </div>

                  {errorMsg && (
                    <p className="text-red-600 text-xs" data-testid="chat-widget-error">
                      {errorMsg}
                    </p>
                  )}

                  <Button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full bg-[#0A192F] hover:bg-[#0A192F]/90 text-white py-5 text-sm font-semibold"
                    data-testid="chat-widget-submit"
                  >
                    {status === "submitting" ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send my proposal request
                      </>
                    )}
                  </Button>

                  <p className="text-[10px] text-[#57534E]/60 text-center leading-relaxed pt-1">
                    By submitting, you consent to Ridgemont Financial contacting you about your proposal.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </>
      )}
    </>
  );
};

export default ChatWidget;
